import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Select,
  Divider,
  ListItemText,
  ListItemIcon,
  Alert,
  AlertTitle,
  Pagination,
  Tooltip,
  Card,
  Stack,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  VisibilityOutlined as VisibilityIcon,
  Clear as ClearIcon,
  DeleteOutlined as DeleteIcon,
  FilterList as FilterListIcon,
  PlayArrowOutlined as PlayArrowIcon,
  CheckCircleOutline as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Add as AddIcon,
  DriveFileMoveOutlined as MoveIcon,
  EditOutlined as EditIcon,
  FolderOutlined as FolderIcon
} from '@mui/icons-material';
import {
  Record as RecordIcon,
  Microphone as MicrophoneIcon,
  PlusCircle,
  FileText,
  Waveform as WaveformIcon,
  Image as ImageIconPhosphor
} from '@phosphor-icons/react';
import { useHeaderContext } from '@/contexts/HeaderContext'; // Adjust path as needed
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid as isValidDate, isToday, isYesterday } from 'date-fns'; // For date formatting
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { getAllCompanyDocuments } from '@/services/companyDocumentService';
import { DocumentResult } from '@/types/companyDocumentTypes';
import {
  deleteTranscriptionSession,
  updateTranscriptionSession
} from '@/services/transcriptionSessionService';
import { getSessionDocuments } from '@/services/transcriptionDocumentService';
import { TranscriptionDocument } from '@/types/transcriptionDocumentTypes';
import {
  getCollectionsByCompany,
  addSessionToCollection,
  removeSessionFromCollection
} from '@/services/sessionCollectionService';
import { SessionCollection } from '@/types/sessionCollectionTypes';
import {
  approveSession,
  rejectSession,
  canUserApprove,
  setSessionRequiresApproval
} from '@/services/approvalService';
import {
  TranscriptionSession,
  TranscriptionSessionStatus
} from '@/types/transcriptionSessionTypes';
import sessionNavigationService from '@/services/sessionNavigationService';
import AttioTable, { SortDirection } from '@/components/AttioTable/AttioTable';
import {
  SortIconDropdown,
  SortOption
} from '@/components/SortIconDropdown/SortIconDropdown';
import { AttioTextField } from '@/components/AttioForm/AttioTextField';
import { formatDuration } from '@/types/transcriptionDocumentTypes';
import { EmptyState } from '@/components/EmptyState';
import { Record } from '@phosphor-icons/react';

// --- Helper Functions ---
const getSessionDisplayName = (session: TranscriptionSession): string => {
  if (session.title) {
    return session.title;
  }
  if (session.templateId) {
    return `Session - Template ${session.templateId}`;
  }
  return 'Untitled Session';
};

// duration deprecated

const isSessionActive = (session: TranscriptionSession): boolean => {
  return (
    session.status === TranscriptionSessionStatus.Draft ||
    session.status === TranscriptionSessionStatus.Recording ||
    session.status === TranscriptionSessionStatus.Processing ||
    session.status === TranscriptionSessionStatus.PendingApproval
  );
};

const formatDateDisplay = (
  dateString?: string | null,
  dateFormat = 'PPp'
): string => {
  if (!dateString) return 'N/A';
  const date = parseISO(dateString);
  return isValidDate(date) ? format(date, dateFormat) : 'Invalid Date';
};

// Smart date display: Today, Yesterday, or short date
const formatSmartDate = (dateString?: string | null): string => {
  if (!dateString) return '';
  const date = parseISO(dateString);
  if (!isValidDate(date)) return '';
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'M.dd.yy');
};

// Get user initials from name (e.g., "John Smith" -> "JS", "vibhavkalyan11" -> "V")
const getUserInitials = (name?: string | null): string => {
  if (!name) return '';
  const parts = name.trim().split(/[\s._@]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

// Get file type icon based on file format/extension (unfilled Phosphor icons)
const getFileTypeIcon = (fileFormat?: string | null, size: number = 16) => {
  const ext = fileFormat?.toLowerCase().replace('.', '') || '';
  
  // Audio files
  if (['mp3', 'wav', 'm4a', 'ogg', 'webm', 'aac', 'flac'].includes(ext)) {
    return <WaveformIcon size={size} weight="regular" />;
  }
  // Image files
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
    return <ImageIconPhosphor size={size} weight="regular" />;
  }
  // Default to document icon
  return <FileText size={size} weight="regular" />;
};

type Order = 'asc' | 'desc';
type SortableRecordKeys = 'title' | 'sessionStartTime';

// --- RecordsPage Component ---
export const RecordsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const navigate = useNavigate();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser, selectedUserRole } = useAppUser();

  const [documents, setDocuments] = useState<DocumentResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState<'my' | 'all'>('all');
  const [fileTypeFilter, setFileTypeFilter] = useState<
    'all' | 'audio' | 'image' | 'document'
  >('all');
  // sessionDocuments might not be needed if we get documents directly, but keeping for now if needed for other logic
  const [sessionDocuments, setSessionDocuments] = useState<
    Record<string, TranscriptionDocument[]>
  >({});
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [canApprove, setCanApprove] = useState(false);
  const [recordActionMenuAnchorEl, setRecordActionMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedRecordForAction, setSelectedRecordForAction] =
    useState<DocumentResult | null>(null);
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [moveToCollectionDialogOpen, setMoveToCollectionDialogOpen] =
    useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [availableCollections, setAvailableCollections] = useState<
    SessionCollection[]
  >([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [isDeletingRecord, setIsDeletingRecord] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<DocumentResult | null>(
    null
  );

  // Server-side pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [sessionsPerPage] = useState(20); // Fixed page size for server-side pagination
  const [orderBy, setOrderBy] =
    useState<SortableRecordKeys>('sessionStartTime');
  const [order, setOrder] = useState<Order>('desc');

  // Mobile infinite scroll state
  const [allDocuments, setAllDocuments] = useState<DocumentResult[]>([]); // Accumulated documents for mobile
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Sort options for the new dropdown
  const sortOptions: SortOption[] = [
    {
      key: 'sessionStartTime',
      label: 'Date',
      ascendingLabel: 'First',
      descendingLabel: 'Last'
    },
    {
      key: 'title',
      label: 'Title',
      ascendingLabel: 'A-Z',
      descendingLabel: 'Z-A'
    }
  ];

  // Fetch documents for sessions (for file type filtering) - DEPRECATED/ADAPTED logic may be needed
  // But for now, let's skip it as we fetch documents directly.

  // Fetch company documents with server-side pagination
  const fetchDocuments = useCallback(
    async (
      pageNum?: number,
      search?: string,
      sortBy?: string,
      sortOrder?: 'asc' | 'desc',
      append: boolean = false // For infinite scroll - append to existing list
    ) => {
      if (!appUser) return;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Use provided parameters or fall back to current state
      const currentPage = pageNum ?? page;
      const currentSearch = search ?? searchQuery;
      // Sort params not fully supported by backend yet, but we pass them if we add support later

      try {
        console.log('Fetching documents with params:', {
          pageNumber: currentPage,
          pageSize: sessionsPerPage,
          searchQuery: currentSearch || undefined
        });

        const paginatedResponse = await getAllCompanyDocuments(
          {
            pageNumber: currentPage,
            pageSize: sessionsPerPage,
            searchQuery: currentSearch || undefined,
            companyId: selectedUserRole?.companyId
          },
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        setDocuments(paginatedResponse.documents);
        setTotalDocuments(paginatedResponse.totalCount);
        setTotalPages(paginatedResponse.totalPages);

        // For mobile infinite scroll: append or reset allDocuments
        if (append) {
          setAllDocuments(prev => [...prev, ...paginatedResponse.documents]);
        } else {
          setAllDocuments(paginatedResponse.documents);
        }
      } catch (err) {
        console.error('Error fetching documents:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load documents'
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [
      appUser,
      selectedUserRole,
      getAccessTokenSilently,
      getAccessTokenWithPopup,
      sessionsPerPage
    ]
  );

  // Load more for mobile infinite scroll
  const loadMore = useCallback(() => {
    if (loadingMore || loading || page >= totalPages) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchDocuments(nextPage, searchQuery, orderBy, order, true);
  }, [loadingMore, loading, page, totalPages, searchQuery, orderBy, order, fetchDocuments]);

  // Check user approval permissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (appUser?.id) {
        const canApproveResult = await canUserApprove(appUser.id);
        setCanApprove(canApproveResult);
      }
    };
    checkPermissions();
  }, [appUser]);

  // Listen for approval status changes
  useEffect(() => {
    const handleApprovalChange = () => {
      // Refresh documents to show updated collections
      fetchDocuments();
    };

    window.addEventListener('approvalStatusChanged', handleApprovalChange);
    return () => {
      window.removeEventListener('approvalStatusChanged', handleApprovalChange);
    };
  }, [fetchDocuments]);

  useEffect(() => {
    setHeaderText(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <FileText size={16} weight="regular" />
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          Files
        </Typography>
      </Box>
    );
    setMenuActions(
      isMobile ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() =>
              sessionNavigationService.navigateToNewSession(navigate)
            }
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#fff',
              borderRadius: '6px',
              width: 36,
              height: 36,
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#d1d5db'}`,
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#f9fafb',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : '#9ca3af'
              }
            }}
          >
            <MicrophoneIcon size={18} weight="regular" />
          </IconButton>
          <IconButton
            onClick={() => navigate('/create/upload')}
            sx={{
              backgroundColor: '#e6f226',
              color: '#030766',
              borderRadius: '6px',
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: '#d4e022'
              }
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<MicrophoneIcon size={18} weight="regular" />}
            onClick={() =>
              sessionNavigationService.navigateToNewSession(navigate)
            }
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#fff',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#d1d5db',
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f9fafb',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : '#9ca3af'
              }
            }}
          >
            Record
          </Button>
          <Button
            variant="contained"
            startIcon={<PlusCircle size={18} weight="regular" />}
            onClick={() => navigate('/create/upload')}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              backgroundColor: '#e6f226',
              color: '#030766',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#d4e022',
                boxShadow: 'none'
              }
            }}
          >
            Upload
          </Button>
        </Box>
      )
    );
    return () => {
      setHeaderText('');
      setMenuActions(null);
    };
  }, [setHeaderText, setMenuActions, isMobile, navigate, theme.palette.mode]);

  // Fetch documents when app user changes
  useEffect(() => {
    if (appUser) {
      fetchDocuments(1, searchQuery, orderBy, order);
    }
  }, [appUser, fetchDocuments]);

  // Sorting is driven by AttioTable's header; we map to server-side sort
  const handleSortChange = (key: string, dir: SortDirection) => {
    const mappedKey: SortableRecordKeys =
      key === 'title' ? 'title' : 'sessionStartTime';
    const mappedDir: Order = dir === 'asc' ? 'asc' : 'desc';
    setOrder(mappedDir);
    setOrderBy(mappedKey);
    setPage(1);
    setAllDocuments([]); // Reset infinite scroll on sort change
    fetchDocuments(1, searchQuery, mappedKey, mappedDir);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    fetchDocuments(newPage, searchQuery, orderBy, order);
  };

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setPage(1);
    setAllDocuments([]); // Reset infinite scroll on search
  }, []);

  // Debounce search: refetch after user stops typing
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchDocuments(1, searchQuery, orderBy, order);
    }, 800);
    return () => clearTimeout(timeout);
  }, [searchQuery, orderBy, order, fetchDocuments]);

  // Mobile infinite scroll: IntersectionObserver to auto-load more
  useEffect(() => {
    if (!isMobile) return;

    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !loadingMore && !loading && page < totalPages) {
          loadMore();
        }
      },
      { rootMargin: '100px' } // Trigger 100px before reaching the bottom
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isMobile, loadingMore, loading, page, totalPages, loadMore]);

  // Helper function to determine file type from file format
  const getFileTypeFromFormat = (
    fileFormat?: string
  ): 'audio' | 'image' | 'document' | null => {
    if (!fileFormat) return null;
    const ext = fileFormat.toLowerCase().replace(/^\./, '');

    if (
      [
        'mp3',
        'wav',
        'm4a',
        'ogg',
        'flac',
        'aac',
        'wma',
        'mp4',
        'webm'
      ].includes(ext)
    ) {
      return 'audio';
    }
    if (
      [
        'jpg',
        'jpeg',
        'png',
        'gif',
        'webp',
        'svg',
        'bmp',
        'heic',
        'heif'
      ].includes(ext)
    ) {
      return 'image';
    }
    if (['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt', 'pages'].includes(ext)) {
      return 'document';
    }
    return null;
  };

  // Filter documents by file type
  // On mobile, use accumulated allDocuments for infinite scroll; on desktop, use paginated documents
  const visibleDocuments = useMemo(() => {
    const sourceDocuments = isMobile ? allDocuments : documents;
    
    if (fileTypeFilter === 'all') {
      return sourceDocuments;
    }

    return sourceDocuments.filter((doc) => {
      const docFileType = getFileTypeFromFormat(doc.fileFormat);
      return docFileType === fileTypeFilter;
    });
  }, [isMobile, allDocuments, documents, fileTypeFilter]);

  const handleOpenRecordActionMenu = (
    event: React.MouseEvent<HTMLElement>,
    document: DocumentResult
  ) => {
    setRecordActionMenuAnchorEl(event.currentTarget);
    setSelectedRecordForAction(document);
  };

  const handleCloseRecordActionMenu = () => {
    setRecordActionMenuAnchorEl(null);
    setSelectedRecordForAction(null);
  };

  const handleOpenDeleteConfirmDialog = (record: DocumentResult) => {
    setRecordToDelete(record);
    setDeleteConfirmDialogOpen(true);
    handleCloseRecordActionMenu();
  };

  const handleConfirmDelete = async () => {
    if (!appUser || !recordToDelete) return;

    setIsDeletingRecord(true);
    try {
      if (recordToDelete.sessionId) {
        // Delete session (which also deletes linked documents)
        const success = await deleteTranscriptionSession(
          recordToDelete.sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        if (success) {
          // Remove from local state
          setDocuments((prev) =>
            prev.filter((d) => d.sessionId !== recordToDelete.sessionId)
          );
        }
      } else {
        // TODO: Implement standalone document deletion when API is available
        console.warn('Standalone document deletion not yet implemented');
        setError('Cannot delete standalone files at this time');
      }
    } catch (err) {
      console.error('Error deleting:', err);
      setError(
        recordToDelete.sessionId
          ? 'Failed to delete session'
          : 'Failed to delete file'
      );
    } finally {
      setIsDeletingRecord(false);
      setDeleteConfirmDialogOpen(false);
      setRecordToDelete(null);
    }
  };

  const handleViewSession = (docId: string) => {
    const doc = documents.find((d) => d.id === docId);
    if (doc && doc.sessionId) {
      sessionNavigationService.navigateToSession(doc.sessionId, navigate);
    } else if (doc) {
      // Navigate to file view
      navigate(`/file/document/${doc.id}`);
    }
    handleCloseRecordActionMenu();
  };

  // Approval action handlers
  const handleApproveSession = async (sessionId: string, notes?: string) => {
    try {
      await approveSession({ sessionId, notes });
      // Document list will refresh via event listener
    } catch (error) {
      console.error('Failed to approve session:', error);
      // TODO: Show error toast
    }
  };

  const handleRejectSession = async (sessionId: string, notes?: string) => {
    try {
      await rejectSession({ sessionId, notes });
      // Document list will refresh via event listener
    } catch (error) {
      console.error('Failed to reject session:', error);
      // TODO: Show error toast
    }
  };

  const handleToggleRequiresApproval = async (
    sessionId: string,
    currentRequiresApproval: boolean
  ) => {
    try {
      await setSessionRequiresApproval(sessionId, !currentRequiresApproval);
      // Document list will refresh via event listener
      handleCloseRecordActionMenu();
    } catch (error) {
      console.error('Failed to toggle approval requirement:', error);
      // TODO: Show error toast
    }
  };

  const handleOpenMoveToCollectionDialog = async () => {
    if (!selectedRecordForAction || !appUser || !selectedUserRole) return;

    handleCloseRecordActionMenu();

    try {
      // Fetch available collections
      const collections = await getCollectionsByCompany(
        selectedUserRole.companyId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setAvailableCollections(collections);

      // Set current collection if session is in one
      // For documents, we check linkedCollections. Logic might need to be adapted for multi-collection documents
      const currentCollections =
        selectedRecordForAction.linkedCollections || [];
      if (currentCollections.length > 0) {
        setSelectedCollectionId(currentCollections[0].id);
      } else {
        setSelectedCollectionId('');
      }

      setMoveToCollectionDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch collections:', error);
      setError('Failed to load collections');
    }
  };

  const handleMoveToCollection = async () => {
    if (!selectedRecordForAction || !appUser) return;

    // Only support moving if linked to a session for now, or implement document move
    if (!selectedRecordForAction.sessionId) {
      console.warn('Moving standalone documents not fully supported yet');
      setMoveToCollectionDialogOpen(false);
      return;
    }

    try {
      const currentCollections =
        selectedRecordForAction.linkedCollections || [];
      const currentCollectionId =
        currentCollections.length > 0 ? currentCollections[0].id : null;

      // If the selected collection is the same as the current one, do nothing
      if (currentCollectionId === selectedCollectionId) {
        setMoveToCollectionDialogOpen(false);
        handleCloseRecordActionMenu();
        return;
      }

      // Remove from current collection if exists and different from selected
      if (currentCollectionId && currentCollectionId !== selectedCollectionId) {
        await removeSessionFromCollection(
          currentCollectionId,
          selectedRecordForAction.sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      }

      // Add to new collection if one is selected and different from current
      if (
        selectedCollectionId &&
        selectedCollectionId !== currentCollectionId
      ) {
        await addSessionToCollection(
          selectedCollectionId,
          selectedRecordForAction.sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      }

      // Refresh documents to show updated collections
      await fetchDocuments();

      setMoveToCollectionDialogOpen(false);
      handleCloseRecordActionMenu();
    } catch (error) {
      console.error('Failed to move session:', error);
      setError('Failed to move session to collection');
    }
  };

  const handleOpenRenameDialog = () => {
    if (!selectedRecordForAction) return;
    handleCloseRecordActionMenu();
    setRenameValue(selectedRecordForAction.documentName);
    setRenameDialogOpen(true);
  };

  const handleRename = async () => {
    if (!selectedRecordForAction || !appUser || !renameValue.trim()) return;

    // Only support renaming session-linked documents via session update for now
    if (selectedRecordForAction.sessionId) {
      try {
        await updateTranscriptionSession(
          selectedRecordForAction.sessionId,
          {
            id: selectedRecordForAction.sessionId,
            title: renameValue.trim()
          },
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        // Update local state
        setDocuments((prev) =>
          prev.map((d) =>
            d.id === selectedRecordForAction.id
              ? {
                  ...d,
                  documentName: renameValue.trim(),
                  sessionTitle: renameValue.trim()
                }
              : d
          )
        );

        setRenameDialogOpen(false);
        handleCloseRecordActionMenu();
      } catch (error) {
        console.error('Failed to rename session:', error);
        setError('Failed to rename session');
      }
    } else {
      // TODO: Implement direct document rename
      console.warn('Renaming standalone documents not implemented yet');
      setRenameDialogOpen(false);
    }
  };

  // Helper function to determine file type from file format
  // (Already defined above, keeping duplicate check)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Search and Filter Controls Section */}
      <Box
        sx={{
          position: 'sticky',
          top: isMobile ? '56px' : '64px',
          zIndex: 10,
          background: theme.palette.background.paper,
          px: isMobile ? 2 : 3,
          pt: 2,
          pb: 0
        }}
      >
        {/* Search and Sort Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2
          }}
        >
          <TextField
            placeholder="Search documents..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                height: 40,
                '&:hover': {
                  borderColor:
                    theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                },
                '&.Mui-focused': {
                  borderColor: '#6b7280',
                  boxShadow: 'none'
                }
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ fontSize: 18, color: theme.palette.text.secondary }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => handleSearch('')}
                    edge="end"
                  >
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <SortIconDropdown
            options={sortOptions}
            currentSort={orderBy}
            currentDirection={order}
            onSortChange={(key, direction) => {
              if (key && direction) {
                setOrderBy(key as SortableRecordKeys);
                setOrder(direction);
              }
            }}
            size={isMobile ? 'medium' : 'small'}
          />

          <Button
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              height: 40,
              width: 40,
              minWidth: 40,
              px: 0,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: showFilters
                ? 'rgba(75, 94, 219, 0.04)'
                : theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'white',
              color: showFilters ? '#4B5EDB' : theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: showFilters
                  ? 'rgba(75, 94, 219, 0.08)'
                  : theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : '#f8f9fa',
                borderColor: showFilters
                  ? '#4B5EDB'
                  : theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.2)'
                  : '#d1d5db'
              }
            }}
          >
            <FilterListIcon sx={{ fontSize: '18px' }} />
          </Button>
        </Box>

        {/* Filters - Only show when filters are enabled */}
        {showFilters && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            {/* Owner Filter */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { key: 'my', label: 'My Files' },
                { key: 'all', label: 'All Files' }
              ].map((tab) => (
                <Button
                  key={tab.key}
                  onClick={() => {
                    setOwnerFilter(tab.key as 'my' | 'all');
                    setPage(1); // Reset to first page when changing filter
                    setAllDocuments([]); // Reset infinite scroll
                  }}
                  sx={{
                    px: 2.5,
                    py: 0.75,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    minHeight: 32,
                    background:
                      ownerFilter === tab.key
                        ? theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : '#1f2937'
                        : theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : '#f3f4f6',
                    color:
                      ownerFilter === tab.key
                        ? theme.palette.mode === 'dark'
                          ? 'white'
                          : 'white'
                        : theme.palette.text.secondary,
                    border: 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      background:
                        ownerFilter === tab.key
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.15)'
                            : '#111827'
                          : theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : '#e5e7eb'
                    }
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </Box>

            {/* File Type Filter */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {[
                { key: 'all', label: 'All' },
                { key: 'audio', label: 'Audio' },
                { key: 'image', label: 'Image' },
                { key: 'document', label: 'Document' }
              ].map((filter) => (
                <Button
                  key={filter.key}
                  onClick={() => {
                    setFileTypeFilter(
                      filter.key as 'all' | 'audio' | 'image' | 'document'
                    );
                    setPage(1); // Reset to first page when changing filter
                    setAllDocuments([]); // Reset infinite scroll
                  }}
                  sx={{
                    px: 2.5,
                    py: 0.75,
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 500,
                    minHeight: 32,
                    background:
                      fileTypeFilter === filter.key
                        ? theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : '#1f2937'
                        : theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : '#f3f4f6',
                    color:
                      fileTypeFilter === filter.key
                        ? theme.palette.mode === 'dark'
                          ? 'white'
                          : 'white'
                        : theme.palette.text.secondary,
                    border: 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      background:
                        fileTypeFilter === filter.key
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.15)'
                            : '#111827'
                          : theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.08)'
                          : '#e5e7eb'
                    }
                  }}
                >
                  {filter.label}
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Main Content Section */}
      <Box
        sx={{
          px: isMobile ? 2 : 3,
          pt: 0,
          pb: 3,
          background: theme.palette.background.paper
        }}
      >
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}

        {loading && visibleDocuments.length === 0 ? (
          <Stack spacing={1}>
            {[1, 2, 3, 4].map((i) => (
              <Card
                key={i}
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5
                }}
              >
                <Skeleton 
                  variant="rounded" 
                  width={32} 
                  height={32} 
                  sx={{ 
                    borderRadius: '4px',
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.06)'
                  }} 
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton 
                    width="50%" 
                    height={20} 
                    sx={{ 
                      mb: 0.5,
                      bgcolor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.05)' 
                        : 'rgba(0,0,0,0.06)'
                    }} 
                  />
                  <Skeleton 
                    width="35%" 
                    height={16} 
                    sx={{ 
                      bgcolor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.05)' 
                        : 'rgba(0,0,0,0.06)'
                    }} 
                  />
                </Box>
                <Skeleton 
                  variant="rounded" 
                  width={70} 
                  height={24} 
                  sx={{ 
                    borderRadius: '12px',
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.06)'
                  }} 
                />
              </Card>
            ))}
          </Stack>
        ) : visibleDocuments.length > 0 ? (
          <Stack spacing={1}>
            {visibleDocuments.map((doc, index) => (
              <Card
                key={doc.id}
                onClick={() => handleViewSession(doc.id)}
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer',
                  opacity: 0,
                  animation: 'fadeSlideIn 0.3s ease forwards',
                  animationDelay: `${index * 30}ms`,
                  '@keyframes fadeSlideIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  },
                  '&:hover': {
                    borderColor:
                      theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.02)'
                        : '#fafafa'
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                    transition: 'transform 0.1s ease'
                  }
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  {/* Left Side - Document Info */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      flex: 1,
                      minWidth: 0
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : '#f3f4f6',
                        color: '#6b7280',
                        flexShrink: 0
                      }}
                    >
                      {getFileTypeIcon(doc.fileFormat, 14)}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Title - prefer sessionTitle (AI/user title), fallback to documentName */}
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          textAlign: 'left',
                          mb: 0.5,
                          lineHeight: 1.4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {doc.sessionTitle || doc.documentName}
                      </Typography>

                      {/* Metadata Row - Order: Date → Folders → User → File type */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          flexWrap: 'nowrap',
                          overflow: 'hidden'
                        }}
                      >
                        {/* 1. Date (Today/Yesterday/date) */}
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '12px',
                            color: theme.palette.text.secondary,
                            textAlign: 'left',
                            flexShrink: 0
                          }}
                        >
                          {formatSmartDate(doc.createdAt)}
                        </Typography>

                        {/* 2. Folders Count Tag */}
                        {doc.linkedCollections.length > 0 && (
                          <Chip
                            label={`${doc.linkedCollections.length} ${doc.linkedCollections.length === 1 ? 'Folder' : 'Folders'}`}
                            size="small"
                            icon={<FolderIcon sx={{ fontSize: 12 }} />}
                            sx={{
                              height: 22,
                              borderRadius: '4px',
                              fontSize: '11px',
                              backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.06)'
                                : '#f5f5f5',
                              color: theme.palette.text.secondary,
                              '& .MuiChip-label': { px: 1, pr: 1.5 },
                              '& .MuiChip-icon': { 
                                color: theme.palette.text.secondary,
                                marginLeft: '6px',
                                marginRight: '-2px'
                              }
                            }}
                          />
                        )}

                        {/* 3. User initials tag */}
                        {doc.uploadedByUserName && (
                          <Chip
                            label={getUserInitials(doc.uploadedByUserName)}
                            size="small"
                            title={doc.uploadedByUserName}
                            sx={{
                              height: 22,
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 500,
                              minWidth: 32,
                              backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.06)'
                                : '#f5f5f5',
                              color: theme.palette.text.secondary,
                              '& .MuiChip-label': { px: 1 }
                            }}
                          />
                        )}

                        {/* 4. File Type Tag */}
                        {(() => {
                          // Extract file extension from documentName or fileFormat
                          const getFileExtension = () => {
                            if (doc.fileFormat) {
                              return doc.fileFormat.startsWith('.') 
                                ? doc.fileFormat.toUpperCase() 
                                : `.${doc.fileFormat.toUpperCase()}`;
                            }
                            const ext = doc.documentName.split('.').pop();
                            return ext ? `.${ext.toUpperCase()}` : null;
                          };
                          const fileExt = getFileExtension();
                          return fileExt ? (
                            <Chip
                              label={fileExt}
                              size="small"
                              sx={{
                                height: 22,
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 500,
                                backgroundColor: theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.06)'
                                  : '#f5f5f5',
                                color: theme.palette.text.secondary,
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          ) : null;
                        })()}
                      </Box>
                    </Box>
                  </Box>

                  {/* Right Side - Actions Menu */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenRecordActionMenu(e, doc);
                      }}
                      sx={{
                        borderRadius: '6px',
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          color: theme.palette.text.primary,
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.08)'
                              : '#f3f4f6'
                        }
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            ))}
          </Stack>
        ) : (
          <EmptyState
            icon={<Record size={22} weight="regular" />}
            title={searchQuery ? 'No documents match your search' : 'No documents found'}
            description={
              searchQuery
                ? 'Try adjusting your search terms'
                : 'Start your first session or upload a document'
            }
          />
        )}

        {/* Mobile: Infinite scroll with Load More fallback */}
        {isMobile && (
          <>
            {/* Loading more indicator */}
            {loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            
            {/* Load more button (fallback if IntersectionObserver misses) */}
            {!loadingMore && page < totalPages && visibleDocuments.length > 0 && (
              <Button
                onClick={loadMore}
                fullWidth
                variant="text"
                sx={{
                  mt: 2,
                  py: 1.5,
                  color: theme.palette.text.secondary,
                  fontSize: '14px',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                Load more
              </Button>
            )}
            
            {/* Sentinel element for IntersectionObserver */}
            <div ref={loadMoreRef} style={{ height: 1 }} />
          </>
        )}

        {/* Desktop: Traditional pagination */}
        {!isMobile && totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Page {page} • {totalDocuments} total
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handleChangePage}
              color="primary"
              size="medium"
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1,
                  fontWeight: 500
                }
              }}
            />
          </Box>
        )}
      </Box>

      {/* Add pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.4; }
            100% { opacity: 1; }
          }
        `}
      </style>

      {/* Add pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0% {
              opacity: 1;
            }
            50% {
              opacity: 0.4;
            }
            100% {
              opacity: 1;
            }
          }
        `}
      </style>

      {/* Record Action Menu */}
      <Menu
        anchorEl={recordActionMenuAnchorEl}
        open={Boolean(recordActionMenuAnchorEl)}
        onClose={handleCloseRecordActionMenu}
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        <MenuItem
          onClick={() => {
            if (selectedRecordForAction)
              handleViewSession(selectedRecordForAction.id);
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            View Details
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedRecordForAction) {
              handleViewSession(selectedRecordForAction.id);
            }
          }}
          disabled={
            !selectedRecordForAction || !selectedRecordForAction.sessionId
          }
        >
          <ListItemIcon>
            <PlayArrowIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Continue Session
          </ListItemText>
        </MenuItem>
        <Divider />

        {/* Rename Menu Item */}
        <MenuItem onClick={handleOpenRenameDialog}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Rename
          </ListItemText>
        </MenuItem>

        {/* Move to Collection Menu Item */}
        <MenuItem onClick={handleOpenMoveToCollectionDialog}>
          <ListItemIcon>
            <MoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            Move to Collection
          </ListItemText>
        </MenuItem>

        <Divider />

        {/* Requires Approval Toggle */}
        {selectedRecordForAction && (
          <MenuItem
            onClick={() => {
              /*
              const session = visibleSessions.find(
                (s) => s.id === selectedRecordForAction.id
              );
              if (session) {
                // For now, allow toggling for all sessions
                // TODO: When backend is ready, check if template controls approval requirement
                handleToggleRequiresApproval(
                  session.id,
                  session.requiresApproval || false
                );
              }
              */
            }}
          >
            <ListItemIcon>
              {(() => {
                /*
                const session = visibleSessions.find(
                  (s) => s.id === selectedRecordForAction.id
                );
                const requiresApproval = session?.requiresApproval || false;
                return requiresApproval ? (
                  <CheckCircleIcon fontSize="small" />
                ) : (
                  <RadioButtonUncheckedIcon fontSize="small" />
                );
                */
                return <RadioButtonUncheckedIcon fontSize="small" />;
              })()}
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
              Requires Approval
            </ListItemText>
          </MenuItem>
        )}

        <Divider />
        <MenuItem
          onClick={() => {
            if (selectedRecordForAction) {
              handleOpenDeleteConfirmDialog(selectedRecordForAction);
            }
          }}
          sx={{ color: theme.palette.error.main }}
        >
          <ListItemIcon>
            <DeleteIcon
              fontSize="small"
              sx={{ color: theme.palette.error.main }}
            />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '14px' }}>
            {selectedRecordForAction?.sessionId
              ? 'Delete Session'
              : 'Delete File'}
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Move to Collection Dialog */}
      <Dialog
        open={moveToCollectionDialogOpen}
        onClose={() => setMoveToCollectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Move to Collection</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Select
              fullWidth
              value={selectedCollectionId}
              onChange={(e) => setSelectedCollectionId(e.target.value)}
              displayEmpty
              sx={{
                mt: 1,
                '& .MuiSelect-select': {
                  py: 1.5
                }
              }}
            >
              <MenuItem value="">
                <em>No Collection</em>
              </MenuItem>
              {availableCollections.map((collection) => (
                <MenuItem key={collection.id} value={collection.id}>
                  {collection.title}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveToCollectionDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleMoveToCollection}
            variant="contained"
            sx={{
              backgroundColor:
                theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              }
            }}
          >
            Move
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px'
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            px: 3,
            pt: 3,
            pb: 0
          }}
        >
          Rename Session
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1.5 }}
          >
            <AttioTextField
              autoFocus
              label="Session Name"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRename();
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 0,
            gap: 1.5
          }}
        >
          <Button
            onClick={() => setRenameDialogOpen(false)}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              px: 2,
              py: 0.75,
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRename}
            variant="contained"
            disabled={!renameValue.trim()}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              px: 2,
              py: 0.75,
              backgroundColor:
                theme.palette.mode === 'dark' ? '#ffffff' : '#1A1A1A',
              color: theme.palette.mode === 'dark' ? '#1A1A1A' : '#ffffff',
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#e5e5e5' : '#000000'
              },
              '&:disabled': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.12)',
                color:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.26)'
                    : 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={() => {
          setDeleteConfirmDialogOpen(false);
          setRecordToDelete(null);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {recordToDelete?.sessionId ? 'Delete Session' : 'Delete File'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete "
            {recordToDelete?.documentName ||
              recordToDelete?.sessionTitle ||
              'this item'}
            "? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={() => {
              setDeleteConfirmDialogOpen(false);
              setRecordToDelete(null);
            }}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              px: 2,
              py: 0.75,
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={isDeletingRecord}
            startIcon={
              isDeletingRecord ? (
                <CircularProgress size={16} sx={{ color: '#ffffff' }} />
              ) : null
            }
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              px: 2,
              py: 0.75,
              backgroundColor: theme.palette.error.main,
              color: '#ffffff',
              '&:hover': {
                backgroundColor: theme.palette.error.dark
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.error.main,
                color: '#ffffff',
                opacity: 0.7
              }
            }}
          >
            {isDeletingRecord
              ? 'Deleting...'
              : recordToDelete?.sessionId
              ? 'Delete Session'
              : 'Delete File'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecordsPage;
